import {
  Box,
  ChevronUpIcon,
  Flex,
  JaneGoldIcon,
  Typography,
} from '@iheartjane/reefer';
import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import type { Nullable } from '@iheartjane/types';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useLoaderData } from 'react-router';

import { Breadcrumbs } from '@jane/bloom/components/breadcrumbs/breadcrumbs';
import { LinkWithBasename } from '@jane/bloom/components/linkWithBasename';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters/provider';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { registerJdid } from '@jane/bloom/services/tracking/mixpanel';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  BrandSpecial,
  BrandSpecials,
} from '@jane/bloom/types/schemas/marketplace/brandSpecials';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { ComplianceLanguage } from '@jane/bloom/types/schemas/roots/complianceLanguage';
import type { BrandSpecialViewModel } from '@jane/bloom/viewModels/brandSpecialViewModel';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type {
  MenuProductImgsAboveTheFold,
  ProductSearchCriteria,
} from '@jane/bloom/viewModels/productSearch';
import type { ProductSearchFiltersViewModel } from '@jane/bloom/viewModels/productSearchFilters';
import type { SortViewModel } from '@jane/bloom/viewModels/sort.server';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';

import { GoldInfoBox } from './components/goldInfoBox';
import { GoldProductGrid } from './components/goldProductGrid';
import styles from './janeGoldDetailComponent.module.css';

interface JaneGoldDetailResults extends Record<string, unknown> {
  applicablePriceIds: ProductApplicableIds[];
  availableFilters: ProductSearchFiltersViewModel;
  brandSpecials: BrandSpecials;
  brandSpecialWithStatus: BrandSpecialViewModel;
  complianceLanguage?: Nullable<ComplianceLanguage>;
  currentSpecial: BrandSpecial;
  customLanguage: CustomLanguage;
  isSegmentedDeal: boolean;
  isSignedIn: boolean;
  janeGold: JaneGold;
  jdid: string;
  menuProductImgsAboveTheFold: MenuProductImgsAboveTheFold[];
  recommendedSortResults?: string;
  searchCriteria: ProductSearchCriteria;
  sort: SortViewModel;
  storeSpecials: StoreSpecials;
}

export function JaneGoldDetailRouteComponent() {
  const { cart, pinnedStore } = useRootLoaderData();
  const {
    applicablePriceIds,
    availableFilters,
    brandSpecials,
    brandSpecialWithStatus,
    currentSpecial,
    customLanguage,
    isSegmentedDeal,
    isSignedIn,
    janeGold,
    jdid,
    menuProductImgsAboveTheFold,
    recommendedSortResults,
    searchCriteria,
    sort: { currentSort, sortOptions },
    storeSpecials,
  } = useLoaderData<JaneGoldDetailResults>();

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  const bannerLabel =
    !isSignedIn && isSegmentedDeal ? 'Sign in for eligibility' : 'Learn more';

  const mobileMiniTitle = `${currentSpecial?.title} · ${currentSpecial?.description}`;
  const mobileMiniSign =
    mobileMiniTitle.length > 50
      ? `${mobileMiniTitle.substring(0, 50)}...`
      : mobileMiniTitle;

  const [showMiniHeader, setShowMiniHeader] = useState(false);
  const infoBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerJdid(jdid);
    registerJdidToRum(jdid);
  }, [jdid]);

  useEffect(() => {
    const handleScroll = () => {
      if (infoBoxRef.current) {
        const rect = infoBoxRef.current.getBoundingClientRect();
        setShowMiniHeader(rect.bottom < 150);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isBundle = currentSpecial?.special_type !== 'product';

  return (
    <div className={styles['janeGoldDetailMain']}>
      <Box
        aria-label="Scroll to top"
        className={clsx(styles['janeGoldInfoBoxMini'], {
          [styles['visible']]: showMiniHeader,
        })}
        border="grays-light"
        borderRadius="lg"
        onClick={() => {
          window.scrollTo({ behavior: 'smooth', top: 0 });
        }}
      >
        <Flex
          flexDirection="row"
          className={styles['janeGoldInfoBoxMiniContent']}
        >
          <Flex className={styles['janeGoldMiniText']}>
            <JaneGoldIcon className="gold" style={{ marginRight: '8px' }} />
            <Flex flexDirection="column">
              <Typography color="grays-mid">{mobileMiniSign}</Typography>
              {!isSignedIn && isSegmentedDeal && (
                <Typography color="text-main" variant="body-bold">
                  Sign in for eligibility
                </Typography>
              )}
            </Flex>
          </Flex>
          <ChevronUpIcon color="text-main" />
        </Flex>
      </Box>
      <Breadcrumbs
        className={styles['janeGoldDetailBreadcrumbs']}
        breadcrumbs={[
          {
            label: 'Home',
            to: '/',
          },
          {
            label: currentSpecial?.product_brand.name,
            to: `/featured?brand=${currentSpecial?.product_brand.name}`,
          },
          {
            label: 'Gold deals',
            to: '/jane-gold',
          },
        ]}
      />

      <Box
        p={24}
        border="grays-light"
        borderRadius="lg"
        mb={16}
        className={styles['janeGoldDetailInfoBox']}
      >
        <div className={styles['janeGoldDetailInfo']}>
          <Flex className={styles['janeGoldDetailInfoText']}>
            <JaneGoldIcon style={{ marginRight: '8px' }} />
            <Typography variant="body-bold">
              Jane Gold is a way to earn cash back rewards from your favorite
              cannabis brands.
            </Typography>
          </Flex>

          {bannerLabel && (
            <LinkWithBasename to="/account" variant="reefer-underline">
              <Typography>{bannerLabel}</Typography>
            </LinkWithBasename>
          )}
        </div>
      </Box>

      <div className={styles['janeGoldDetailContainer']}>
        <div ref={infoBoxRef}>
          <GoldInfoBox
            isBundle={isBundle}
            brand={currentSpecial?.product_brand.name}
            title={currentSpecial?.title}
            description={currentSpecial?.description ?? ''}
            terms={currentSpecial?.terms ?? ''}
            brandLogo={currentSpecial?.product_brand.logo_url ?? ''}
            tagLabel={currentSpecial?.discount_label ?? ''}
            brandSpecial={brandSpecialWithStatus}
          />
        </div>

        <BloomFilterProvider
          availableFilters={availableFilters}
          searchCriteria={searchCriteria}
        >
          <GoldProductGrid
            rowInstance={rowInstance}
            preloadedProductIds={menuProductImgsAboveTheFold.map(
              ({ id }) => id
            )}
            cart={cart}
            brandSpecials={brandSpecials}
            customLanguage={customLanguage}
            currentSort={currentSort}
            sortOptions={sortOptions}
            pinnedStore={pinnedStore || ({} as StoreViewModel)}
            janeGold={janeGold}
            storeSpecials={storeSpecials}
            applicablePriceIds={applicablePriceIds}
          />
        </BloomFilterProvider>
      </div>
    </div>
  );
}
